import type { CashbackLedgerEntry } from '@erp/shared';
import { Gift } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/shadcn/index.js';
import { Card, DataTable, type DataTableColumn, EmptyState, Skeleton, StatCard } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatDate } from '../../lib/intl.js';
import { formatDecimals } from '../../lib/money.js';

interface CashbackPortalData {
  balance: string;
  history: CashbackLedgerEntry[];
  /** Tenant base currency the wallet is denominated in (null on older API). */
  currencyCode: string | null;
}

export function PortalCashbackPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [data, setData] = useState<CashbackPortalData | null>(null);
  const [error, setError] = useState(false);
  // Decimals follow the wallet's base currency (registered by the portal layout).
  const num = (n: string): string => formatDecimals(n, data?.currencyCode, i18n.language);

  useEffect(() => {
    void customerApi()
      .get<CashbackPortalData>('/cashback')
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const columns: DataTableColumn<CashbackLedgerEntry>[] = [
    { id: 'date', header: t('cashback.columns.date', { defaultValue: 'Date' }), sortValue: (r) => r.createdAt, cell: (r) => formatDate(r.createdAt, { locale: i18n.language }) },
    {
      id: 'type', header: t('cashback.columns.type', { defaultValue: 'Type' }),
      cell: (r) => <Badge variant="secondary">{t(`cashback.type.${r.type}`, { defaultValue: r.type })}</Badge>,
    },
    {
      id: 'amount', header: t('cashback.columns.amount', { defaultValue: 'Amount' }), align: 'end',
      cellClassName: 'font-mono tabular-nums',
      sortValue: (r) => Number(r.amount),
      cell: (r) => <span className={Number(r.amount) >= 0 ? 'text-success' : 'text-danger'}>{Number(r.amount) > 0 ? '+' : ''}{num(r.amount)}</span>,
    },
    { id: 'balance', header: t('cashback.columns.balance', { defaultValue: 'Balance' }), align: 'end', cellClassName: 'font-mono tabular-nums text-fg-muted', cell: (r) => num(r.balanceAfter) },
  ];

  if (error) return <EmptyState title={t('errors.load', { defaultValue: 'Could not load your cashback.' })} />;
  if (!data) return <Card><Skeleton className="h-6 w-40" /><Skeleton className="mt-4 h-32 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('cashback.title', { defaultValue: 'Cashback' })}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label={t('cashback.balance', { defaultValue: 'Available cashback' })} value={num(data.balance)} icon={Gift} tone="success" />
      </div>
      {data.history.length === 0 ? (
        <EmptyState title={t('cashback.empty', { defaultValue: 'No cashback activity yet.' })} />
      ) : (
        <Card className="p-0">
          <DataTable columns={columns} data={data.history} rowKey={(r) => r.id} searchable={false} />
        </Card>
      )}
    </div>
  );
}
